import { useMemo } from "preact/hooks";
import type { MapStand } from "../shared/types";
import { mapPolygonBoundary, mapPolygonCornerIndices } from "./venueMapPolygon";
import { orderMapFaces } from "./venueMapFaceOrder";
import { mapStandHeight } from "./venueMapProjection";
import { mapStandColors, mapStandBorderColor } from "./venueMapAppearance";
import { VenueMapBoothLabel } from "./VenueMapBoothLabel";
import { venueMapBoothLabelRotation } from "./venueMapBoothLabelLayout";
import type { VenueMapLogos } from "./useVenueMapLogos";

type Point = { x: number; y: number; z: number };
type Face = {
  key: string;
  points: Point[];
  fill: string;
  roof: boolean;
  corners?: number[];
};

const yaw = (-24 * Math.PI) / 180;
const pitch = (48 * Math.PI) / 180;

function project(point: { x: number; y: number; z?: number }) {
  const elevation = point.z ?? 0;
  const across = point.x * Math.cos(yaw) - point.y * Math.sin(yaw);
  const along = point.x * Math.sin(yaw) + point.y * Math.cos(yaw);
  return {
    x: across,
    y: along * Math.cos(pitch) - elevation * Math.sin(pitch),
    depth: along * Math.sin(pitch) + elevation * Math.cos(pitch),
  };
}

function signedArea(points: { x: number; y: number }[]) {
  return points.reduce((sum, point, index) => {
    const next = points[(index + 1) % points.length];
    return sum + point.x * next.y - next.x * point.y;
  }, 0);
}

function shade(hex: string, amount: number) {
  return `#${[1, 3, 5]
    .map((offset) =>
      Math.round(parseInt(hex.slice(offset, offset + 2), 16) * amount)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;
}

function thumbnailGeometry(stand: MapStand) {
  const vertices = stand.polygons.flat();
  if (!vertices.length) return null;
  const centreX =
    (Math.min(...vertices.map(([x]) => x)) +
      Math.max(...vertices.map(([x]) => x))) /
    2;
  const centreY =
    (Math.min(...vertices.map(([, y]) => y)) +
      Math.max(...vertices.map(([, y]) => y))) /
    2;
  const height = mapStandHeight(stand);
  const roofColor = mapStandColors(stand).roof;
  const footprints = stand.polygons.map((polygon) =>
    mapPolygonBoundary(
      polygon.map(([x, y]) => ({ x: x - centreX, y: y - centreY, z: 0 })),
    ),
  );
  const faces: Face[] = footprints.flatMap((footprint, polygonIndex) => {
    const winding = Math.sign(signedArea(footprint));
    const corners = new Set(mapPolygonCornerIndices(footprint));
    const walls: Face[] =
      height > 0
        ? footprint.flatMap((point, index) => {
            const next = footprint[(index + 1) % footprint.length];
            const points = [
              point,
              next,
              { ...next, z: height },
              { ...point, z: height },
            ];
            if (winding * signedArea(points.map(project)) <= 0) return [];
            const dx = next.x - point.x;
            const dy = next.y - point.y;
            const normalX = winding * dy;
            const normalY = -winding * dx;
            const length = Math.hypot(normalX, normalY);
            if (!length) return [];
            const rotatedX = normalX * Math.cos(yaw) - normalY * Math.sin(yaw);
            const rotatedY = normalX * Math.sin(yaw) + normalY * Math.cos(yaw);
            const light = (-0.6 * rotatedX - 0.8 * rotatedY) / length;
            return [
              {
                key: `${polygonIndex}-wall-${index}`,
                points,
                corners: [0, 1].filter((offset) =>
                  corners.has((index + offset) % footprint.length),
                ),
                fill: shade(roofColor, 0.8 + light * 0.12),
                roof: false,
              },
            ];
          })
        : [];
    return [
      ...walls,
      {
        key: `${polygonIndex}-roof`,
        points: footprint.map((point) => ({ ...point, z: height })),
        fill: roofColor,
        roof: true,
      },
    ];
  });
  const extent = footprints.flatMap((polygon) =>
    polygon.flatMap((point) => [
      project(point),
      project({ ...point, z: height }),
    ]),
  );
  const left = Math.min(...extent.map((point) => point.x));
  const right = Math.max(...extent.map((point) => point.x));
  const top = Math.min(...extent.map((point) => point.y));
  const bottom = Math.max(...extent.map((point) => point.y));
  const scale = Math.min(
    60 / Math.max(1, right - left),
    48 / Math.max(1, bottom - top),
  );
  const transform = `translate(${40 - ((left + right) / 2) * scale} ${35 - ((top + bottom) / 2) * scale}) scale(${scale})`;
  const points = (polygon: Point[]) =>
    polygon
      .map((point) => {
        const position = project(point);
        return `${position.x},${position.y}`;
      })
      .join(" ");
  const labelPoint = project({
    x: stand.label.x - centreX,
    y: stand.label.y - centreY,
    z: height,
  });
  const orderedFaces = orderMapFaces(faces, project);
  const cornerKey = (point: Point) => `${point.x}:${point.y}`;
  const cornerOwners = new Map<string, string>();
  for (const face of orderedFaces) {
    if (face.roof) continue;
    for (const index of face.corners ?? [])
      cornerOwners.set(cornerKey(face.points[index]), face.key);
  }
  return {
    labelTransform: `matrix(${Math.cos(yaw)} ${Math.sin(yaw) * Math.cos(pitch)} ${-Math.sin(yaw)} ${Math.cos(yaw) * Math.cos(pitch)} ${labelPoint.x} ${labelPoint.y}) rotate(${venueMapBoothLabelRotation(stand)})`,
    height,
    transform,
    faces: orderedFaces.map((face) => ({
      ...face,
      points: points(face.points),
      cornerPath: face.roof
        ? ""
        : (face.corners ?? [])
            .filter(
              (index) =>
                cornerOwners.get(cornerKey(face.points[index])) === face.key,
            )
            .map(
              (index) =>
                `M${points([face.points[index], face.points[3 - index]])}`,
            )
            .join(""),
    })),
  };
}

/** A miniature of the selected booth's real footprint and configured relief. */
export function VenueMapThumbnail({
  stand,
  label,
  logos,
}: {
  stand?: MapStand;
  label: string;
  logos?: VenueMapLogos;
}) {
  const geometry = useMemo(
    () => (stand ? thumbnailGeometry(stand) : null),
    [stand],
  );
  const borderColor = stand ? mapStandBorderColor(stand) : undefined;
  return (
    <svg
      className="venue-map-thumbnail"
      width="80"
      height="72"
      viewBox="0 0 80 72"
      role="img"
      aria-label={stand ? `Booth ${stand.id} preview` : `${label} location`}
      focusable="false"
      data-booth-height={geometry?.height}
    >
      <title>{label}</title>
      <rect
        x="0.5"
        y="0.5"
        width="79"
        height="71"
        rx="13"
        fill="#f3f7f8"
        stroke="#e0e8eb"
      />
      {geometry ? (
        <g transform={geometry.transform}>
          {geometry.faces.map((face) => (
            <g key={face.key}>
              <polygon
                points={face.points}
                fill={face.fill}
                stroke={face.roof ? borderColor || "#ffffff" : face.fill}
                strokeOpacity={face.roof && !borderColor ? "0.65" : "1"}
                strokeWidth={face.roof ? "0.8" : "0.3"}
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                data-thumbnail-face={face.roof ? "roof" : "wall"}
              />
              {borderColor && face.cornerPath && (
                <path
                  data-thumbnail-corners="true"
                  d={face.cornerPath}
                  fill="none"
                  stroke={borderColor}
                  strokeWidth="0.8"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              )}
            </g>
          ))}
          {stand && (
            <g
              transform={geometry.labelTransform}
              fill={mapStandColors(stand).ink}
            >
              <VenueMapBoothLabel
                stand={stand}
                logo={stand.logo ? logos?.get(stand.logo.id) : undefined}
              />
            </g>
          )}
        </g>
      ) : (
        <g
          fill="none"
          stroke="#557a70"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path
            d="M53 31c0 10-13 21-13 21S27 41 27 31a13 13 0 0 1 26 0Z"
            fill="#d3e9e1"
          />
          <circle cx="40" cy="31" r="4.5" fill="#fff" />
        </g>
      )}
    </svg>
  );
}
